import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Pencil, Play, Plus, Radar, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { indentedName, SMART_SOURCE_LABEL, SMART_SOURCE_PLACEHOLDERS, useAssignees, useDepartments } from '../lib/automation';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatRelative } from '../lib/format';
import { useCategories } from '../lib/hooks';
import type { SmartRule, SmartRulePreview, SmartRuleSource, WorkOrderCategory, WorkOrderPriority } from '../lib/types';
import { WORK_ORDER_CATEGORY_LABEL, WORK_ORDER_PRIORITY_LABEL } from '../lib/work-orders';
import { Placeholders } from './ActionsEditor';
import { useToast } from './toast';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageLoader,
  Select,
  Switch,
  Table,
  Td,
  Textarea,
  Th,
} from './ui';

type Config = Record<string, unknown>;

const SOURCE_DEFAULTS: Record<SmartRuleSource, { title: string; body: string; category: WorkOrderCategory; config: Config }> = {
  DEVICE_OFFLINE: {
    title: '{{asset.tag}} hasn’t checked in for {{device.daysOffline}} days',
    body: '{{device.model}} ({{device.serial}}) last checked in on {{device.lastSync}}. It’s checked out to {{asset.assignee}}.',
    category: 'HARDWARE',
    config: { platform: 'CHROMEOS', days: 21, onlyCheckedOut: true, orgUnitPath: '' },
  },
  AUTO_UPDATE_EXPIRING: {
    title: 'Plan a replacement for {{asset.tag}}',
    body: '{{device.model}} stops getting ChromeOS updates on {{device.aue}}. It’s checked out to {{asset.assignee}}.',
    category: 'HARDWARE',
    config: { days: 90 },
  },
  WARRANTY_EXPIRING: {
    title: 'Warranty ending for {{asset.tag}}',
    body: '{{asset.name}} ({{asset.model}}) is under warranty until {{asset.warrantyExpires}}. Send in any repairs before then.',
    category: 'OTHER',
    config: { days: 30, categoryId: '' },
  },
  CHECKOUT_OVERDUE: {
    title: 'Collect {{asset.tag}} from {{checkout.assignee}}',
    body: '{{asset.name}} was due back on {{checkout.due}} ({{checkout.daysOverdue}} days ago).',
    category: 'OTHER',
    config: { days: 7 },
  },
  REPEAT_REPAIRS: {
    title: 'Review repeat repairs on {{asset.tag}}',
    body: '{{asset.name}} has had {{tickets.count}} tickets in {{tickets.days}} days. Consider replacing it instead of repairing it again.',
    category: 'HARDWARE',
    config: { tickets: 3, days: 90 },
  },
  LOW_STOCK_PARTS: {
    title: 'Reorder {{part.name}}',
    body: 'Only {{part.onHand}} left on the shelf (low stock at {{part.lowStockAt}}). Part number: {{part.number}}.',
    category: 'OTHER',
    config: {},
  },
  DEPARTED_WITH_DEVICE: {
    title: 'Collect {{devices.count}} device(s) from {{person.name}}',
    body: '{{person.name}} ({{person.type}}, {{person.email}}) is no longer with the school as of {{person.leftOn}}.\n\nStill out:\n{{devices.list}}\n\nCheck each one in when it comes back, or note here where it went.',
    category: 'OTHER',
    config: { days: 3, includeGraduating: false, personType: 'ANY' },
  },
  NETWORK_DEVICE_OFFLINE: {
    title: '{{device.name}} has been offline for {{device.offlineMinutes}} minutes',
    body: '{{device.kind}} {{device.model}} ({{device.serial}}) at {{device.site}} {{device.place}} last answered at {{device.lastSeen}}. Its address was {{device.ip}}.',
    category: 'NETWORK',
    config: { minutes: 60, kind: 'ACCESS_POINT', siteName: '' },
  },
};

function describeSource(source: SmartRuleSource, config: Config) {
  switch (source) {
    case 'DEVICE_OFFLINE': {
      const managed = config.platform === 'MDM' || config.platform === 'APPLE';
      return `${managed ? 'Managed devices' : 'Chromebooks'}: no check-in for ${config.days ?? 21} days${!managed && config.orgUnitPath ? ` in ${config.orgUnitPath}` : ''}${config.onlyCheckedOut === false ? '' : ', checked out only'}`;
    }
    case 'AUTO_UPDATE_EXPIRING':
      return `Updates end within ${config.days ?? 90} days`;
    case 'WARRANTY_EXPIRING':
      return `Warranty ends within ${config.days ?? 30} days`;
    case 'CHECKOUT_OVERDUE':
      return `Overdue by ${config.days ?? 7} days or more`;
    case 'REPEAT_REPAIRS':
      return `${config.tickets ?? 3} or more tickets in ${config.days ?? 90} days`;
    case 'LOW_STOCK_PARTS':
      return 'At or below its low-stock level';
    case 'DEPARTED_WITH_DEVICE': {
      const who = config.personType === 'ANY' ? 'Anyone' : config.personType === 'STUDENT' ? 'Students' : config.personType === 'TEACHER' ? 'Teachers' : 'Staff';
      return `${who} with a device, ${config.days ?? 3} day(s) after the roster says they’ve gone${config.includeGraduating ? ', and this year’s graduating class' : ''}`;
    }
    case 'NETWORK_DEVICE_OFFLINE': {
      const kind = config.kind === 'ANY' ? 'Any equipment' : config.kind === 'SWITCH' ? 'Switches' : config.kind === 'GATEWAY' ? 'Gateways' : 'Access points';
      return `${kind}: offline for ${config.minutes ?? 60} minutes or more${config.siteName ? ` at ${config.siteName}` : ''}`;
    }
  }
}

interface FormState {
  name: string;
  description: string;
  enabled: boolean;
  source: SmartRuleSource;
  config: Config;
  title: string;
  body: string;
  category: WorkOrderCategory;
  priority: WorkOrderPriority;
  departmentId: string;
  assignedToId: string;
  checkEveryHours: number;
  cooldownDays: number;
  maxTicketsPerRun: number;
}

function toForm(rule: SmartRule | null): FormState {
  if (!rule) {
    const defaults = SOURCE_DEFAULTS.DEVICE_OFFLINE;
    return {
      name: '',
      description: '',
      enabled: false,
      source: 'DEVICE_OFFLINE',
      config: defaults.config,
      title: defaults.title,
      body: defaults.body,
      category: defaults.category,
      priority: 'NORMAL',
      departmentId: '',
      assignedToId: '',
      checkEveryHours: 24,
      cooldownDays: 7,
      maxTicketsPerRun: 25,
    };
  }
  return {
    name: rule.name,
    description: rule.description ?? '',
    enabled: rule.enabled,
    source: rule.source,
    config: { ...SOURCE_DEFAULTS[rule.source].config, ...rule.sourceConfig },
    title: rule.title,
    body: rule.body,
    category: rule.category,
    priority: rule.priority,
    departmentId: rule.departmentId ?? '',
    assignedToId: rule.assignedToId ?? '',
    checkEveryHours: rule.checkEveryHours,
    cooldownDays: rule.cooldownDays,
    maxTicketsPerRun: rule.maxTicketsPerRun,
  };
}

const numberInput = (value: unknown) => (value === undefined || value === null ? '' : String(value));

function SourceFields({ form, setConfig }: { form: FormState; setConfig(patch: Config): void }) {
  const categories = useCategories();
  const days = (label: string, hint?: string) => (
    <Field label={label} hint={hint}>
      <Input type="number" min={0} value={numberInput(form.config.days)} onChange={(e) => setConfig({ days: Number(e.target.value) })} className="w-28" />
    </Field>
  );
  switch (form.source) {
    case 'DEVICE_OFFLINE':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Devices">
            <Select value={form.config.platform === 'MDM' || form.config.platform === 'APPLE' ? 'MDM' : 'CHROMEOS'} onChange={(e) => setConfig({ platform: e.target.value })}>
              <option value="CHROMEOS">Chromebooks (Google Admin)</option>
              <option value="MDM">Macs, iPads, and Windows PCs (Jamf, Mosyle, or Intune)</option>
            </Select>
          </Field>
          {days('Days without checking in')}
          {form.config.platform !== 'MDM' && form.config.platform !== 'APPLE' && (
            <Field label="Organizational unit" hint="Optional, e.g. /Students">
              <Input value={String(form.config.orgUnitPath ?? '')} onChange={(e) => setConfig({ orgUnitPath: e.target.value })} />
            </Field>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={form.config.onlyCheckedOut !== false}
              onChange={(e) => setConfig({ onlyCheckedOut: e.target.checked })}
              className="size-4 rounded border-slate-300"
            />
            Only devices checked out to someone (skip spares on the shelf)
          </label>
        </div>
      );
    case 'AUTO_UPDATE_EXPIRING':
      return days('Days before updates end');
    case 'WARRANTY_EXPIRING':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          {days('Days before the warranty ends')}
          <Field label="Device category" hint="Optional">
            <Select value={String(form.config.categoryId ?? '')} onChange={(e) => setConfig({ categoryId: e.target.value })}>
              <option value="">Any category</option>
              {categories.data?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      );
    case 'CHECKOUT_OVERDUE':
      return days('Days overdue');
    case 'REPEAT_REPAIRS':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Tickets for the same device">
            <Input type="number" min={2} value={numberInput(form.config.tickets)} onChange={(e) => setConfig({ tickets: Number(e.target.value) })} className="w-28" />
          </Field>
          {days('Within this many days')}
        </div>
      );
    case 'LOW_STOCK_PARTS':
      return <p className="text-sm text-slate-500">Uses each part’s “Low stock at” level from the Parts page.</p>;
    case 'DEPARTED_WITH_DEVICE':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="People">
            <Select value={String(form.config.personType ?? 'ANY')} onChange={(e) => setConfig({ personType: e.target.value })}>
              <option value="ANY">Students and staff</option>
              <option value="STUDENT">Students</option>
              <option value="TEACHER">Teachers</option>
              <option value="STAFF">Staff</option>
            </Select>
          </Field>
          {days('Days after they’ve gone', 'So a sync that drops someone for a moment doesn’t open tickets')}
          <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={form.config.includeGraduating === true}
              onChange={(e) => setConfig({ includeGraduating: e.target.checked })}
              className="size-4 rounded border-slate-300"
            />
            Include this year’s graduating class, before they leave
          </label>
          <p className="text-xs text-slate-500 sm:col-span-2">
            One ticket per person, however many devices they have. Who counts as gone is what your roster says - see the
            Departures page for the list and why each person is on it.
          </p>
        </div>
      );
    case 'NETWORK_DEVICE_OFFLINE':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Equipment">
            <Select value={String(form.config.kind ?? 'ACCESS_POINT')} onChange={(e) => setConfig({ kind: e.target.value })}>
              <option value="ACCESS_POINT">Access points</option>
              <option value="SWITCH">Switches</option>
              <option value="GATEWAY">Gateways</option>
              <option value="ANY">Any network equipment</option>
            </Select>
          </Field>
          <Field label="Minutes offline" hint="Counted from when a sync first found it offline">
            <Input
              type="number"
              min={5}
              value={numberInput(form.config.minutes)}
              onChange={(e) => setConfig({ minutes: Number(e.target.value) })}
              className="w-28"
            />
          </Field>
          <Field label="Site" hint="Optional - the Aruba Central site name">
            <Input value={String(form.config.siteName ?? '')} onChange={(e) => setConfig({ siteName: e.target.value })} />
          </Field>
          <p className="text-xs text-slate-500 sm:col-span-2">
            Equipment comes from the HPE Aruba New Central integration, which decides how often this is checked: a sync every 5 minutes notices an outage
            faster than an hourly one.
          </p>
        </div>
      );
  }
}

function SmartRuleModal({ rule, open, onClose }: { rule: SmartRule | null; open: boolean; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const departments = useDepartments();
  const assignees = useAssignees();
  const [form, setForm] = useState<FormState>(() => toForm(rule));
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(toForm(rule));
      setError('');
      preview.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rule]);

  const update = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }));
  const body = () => ({
    name: form.name,
    description: form.description,
    enabled: form.enabled,
    source: form.source,
    sourceConfig: form.config,
    title: form.title,
    body: form.body,
    category: form.category,
    priority: form.priority,
    departmentId: form.departmentId || null,
    assignedToId: form.assignedToId || null,
    checkEveryHours: form.checkEveryHours,
    cooldownDays: form.cooldownDays,
    maxTicketsPerRun: form.maxTicketsPerRun,
  });

  const preview = useMutation({
    mutationFn: () => api.post<SmartRulePreview>('/smart-rules/preview', { source: form.source, sourceConfig: form.config }),
    onError: (err) => setError(errorMessage(err)),
  });
  const save = useMutation({
    mutationFn: () => (rule ? api.patch(`/smart-rules/${rule.id}`, body()) : api.post('/smart-rules', body())),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['smart-rules'] });
      toast.success('Smart rule saved');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={rule ? `Edit ${rule.name}` : 'New smart rule'}
      description="Opens a ticket on its own when something in the school needs attention."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="smart-rule-form" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="smart-rule-form" onSubmit={submit} className="space-y-6">
        {error && <Alert tone="red">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label="Name" required>
            <Input value={form.name} maxLength={120} required onChange={(e) => update({ name: e.target.value })} placeholder="e.g. Chromebooks offline for 3 weeks" />
          </Field>
          <div className="sm:mt-6">
            <Switch checked={form.enabled} onChange={(enabled) => update({ enabled })} label="Turned on" />
          </div>
        </div>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Open a ticket when</h3>
          <Select
            value={form.source}
            onChange={(e) => {
              const source = e.target.value as SmartRuleSource;
              const defaults = SOURCE_DEFAULTS[source];
              // A new rule starts from the source's suggested wording; an existing rule keeps its own.
              update({ source, config: defaults.config, ...(rule ? {} : { title: defaults.title, body: defaults.body, category: defaults.category }) });
              preview.reset();
            }}
          >
            {(Object.keys(SMART_SOURCE_LABEL) as SmartRuleSource[]).map((source) => (
              <option key={source} value={source}>
                {SMART_SOURCE_LABEL[source]}
              </option>
            ))}
          </Select>
          <SourceFields form={form} setConfig={(patch) => update({ config: { ...form.config, ...patch } })} />
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-slate-600">
                {preview.data ? `${preview.data.total} ${preview.data.total === 1 ? 'thing matches' : 'things match'} right now` : 'See what matches right now'}
              </p>
              <Button size="sm" icon={<Eye />} loading={preview.isPending} onClick={() => preview.mutate()}>
                Preview matches
              </Button>
            </div>
            {!!preview.data?.items.length && (
              <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-sm text-slate-700">
                {preview.data.items.map((item) => (
                  <li key={item.key} className="truncate">
                    {item.label}
                  </li>
                ))}
                {preview.data.total > preview.data.items.length && <li className="text-xs text-slate-500">and {preview.data.total - preview.data.items.length} more</li>}
              </ul>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">The ticket</h3>
          <Field label="Title" required>
            <Input value={form.title} maxLength={150} required onChange={(e) => update({ title: e.target.value })} />
          </Field>
          <Field label="Details" required>
            <Textarea rows={3} maxLength={5000} required value={form.body} onChange={(e) => update({ body: e.target.value })} />
          </Field>
          <Placeholders names={SMART_SOURCE_PLACEHOLDERS[form.source]} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Type of problem">
              <Select value={form.category} onChange={(e) => update({ category: e.target.value as WorkOrderCategory })}>
                {(Object.keys(WORK_ORDER_CATEGORY_LABEL) as WorkOrderCategory[]).map((category) => (
                  <option key={category} value={category}>
                    {WORK_ORDER_CATEGORY_LABEL[category]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onChange={(e) => update({ priority: e.target.value as WorkOrderPriority })}>
                {(Object.keys(WORK_ORDER_PRIORITY_LABEL) as WorkOrderPriority[]).map((priority) => (
                  <option key={priority} value={priority}>
                    {WORK_ORDER_PRIORITY_LABEL[priority]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Department" hint="Workflows can also route it">
              <Select value={form.departmentId} onChange={(e) => update({ departmentId: e.target.value })}>
                <option value="">No department</option>
                {departments.data?.map((department) => (
                  <option key={department.id} value={department.id}>
                    {indentedName(department)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Assign to">
              <Select value={form.assignedToId} onChange={(e) => update({ assignedToId: e.target.value })}>
                <option value="">Nobody (let workflows assign it)</option>
                {assignees.data?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Checking</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Check every">
              <Select value={String(form.checkEveryHours)} onChange={(e) => update({ checkEveryHours: Number(e.target.value) })}>
                <option value="1">Hour</option>
                <option value="6">6 hours</option>
                <option value="12">12 hours</option>
                <option value="24">Day</option>
                <option value="168">Week</option>
              </Select>
            </Field>
            <Field label="Wait after closing" hint="Days before the same item can get a new ticket">
              <Input type="number" min={0} max={365} value={String(form.cooldownDays)} onChange={(e) => update({ cooldownDays: Number(e.target.value) })} />
            </Field>
            <Field label="Most tickets per check">
              <Input type="number" min={1} max={200} value={String(form.maxTicketsPerRun)} onChange={(e) => update({ maxTicketsPerRun: Number(e.target.value) })} />
            </Field>
          </div>
          <p className="text-xs text-slate-500">An item never gets a second ticket while one is still open.</p>
        </section>
      </form>
    </Modal>
  );
}

export function SmartRulesPanel() {
  const { can } = useAuth();
  const canEdit = can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SmartRule | 'new' | null>(null);
  const [deleting, setDeleting] = useState<SmartRule | null>(null);
  const rules = useQuery({ queryKey: ['smart-rules'], queryFn: () => api.get<SmartRule[]>('/smart-rules') });
  const refresh = () => {
    for (const key of ['smart-rules', 'automation-runs', 'work-orders']) void queryClient.invalidateQueries({ queryKey: [key] });
  };

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.patch(`/smart-rules/${id}`, { enabled }),
    onSuccess: () => refresh(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const run = useMutation({
    mutationFn: (id: string) => api.post<{ matched: number; opened: number; error?: string }>(`/smart-rules/${id}/run`),
    onSuccess: (result) => {
      refresh();
      if (result.error) toast.error(result.error);
      else toast.success(`${result.matched} matched · opened ${result.opened} new ticket${result.opened === 1 ? '' : 's'}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/smart-rules/${id}`),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      toast.success('Smart rule deleted');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader
        title="Smart rules"
        description="Watch devices, checkouts, and parts, and open a ticket when something needs attention, without anyone checking."
        actions={
          canEdit && (
            <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
              New smart rule
            </Button>
          )
        }
      />
      {rules.isLoading ? (
        <PageLoader />
      ) : rules.error ? (
        <ErrorState error={rules.error} onRetry={() => rules.refetch()} />
      ) : rules.data!.length === 0 ? (
        <EmptyState icon={<Radar />} title="No smart rules yet" description="For example: open a ticket when a Chromebook hasn’t checked in for three weeks." />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Rule</Th>
              <Th>Opens a ticket when</Th>
              <Th>Open tickets</Th>
              <Th>Checked</Th>
              <Th className="text-right">On</Th>
              {canEdit && <Th className="text-right">Actions</Th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rules.data!.map((rule) => (
              <tr key={rule.id}>
                <Td className="max-w-xs">
                  <p className="font-medium text-slate-900">{rule.name}</p>
                  <p className="truncate text-xs text-slate-500">{[rule.department?.name, rule.assignedTo?.name].filter(Boolean).join(' · ') || 'No department or assignee'}</p>
                </Td>
                <Td className="text-slate-700">
                  {SMART_SOURCE_LABEL[rule.source]}
                  <p className="text-xs text-slate-500">{describeSource(rule.source, rule.sourceConfig)}</p>
                </Td>
                <Td>{rule.openTickets}</Td>
                <Td className="text-xs whitespace-nowrap text-slate-500">
                  {rule.lastCheckAt ? formatRelative(rule.lastCheckAt) : 'Never'}
                  {rule.enabled && rule.nextCheckAt && <p>Next {formatDateTime(rule.nextCheckAt)}</p>}
                </Td>
                <Td className="text-right">
                  <div className="inline-flex">
                    <Switch
                      checked={rule.enabled}
                      disabled={!canEdit}
                      onChange={(enabled) => toggle.mutate({ id: rule.id, enabled })}
                      label={<span className="sr-only">Turn {rule.enabled ? 'off' : 'on'}</span>}
                    />
                  </div>
                </Td>
                {canEdit && (
                  <Td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" icon={<Play />} loading={run.isPending && run.variables === rule.id} onClick={() => run.mutate(rule.id)}>
                      Run now
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setEditing(rule)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(rule)} aria-label={`Delete ${rule.name}`} />
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <SmartRuleModal open={!!editing} rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending}
        title={`Delete “${deleting?.name}”?`}
        description="Tickets it already opened stay open. Its run history is kept."
        confirmLabel="Delete rule"
      />
    </Card>
  );
}
